// assets/cart.fees.js
window.CartMethodsFees = {

    /**
     * Get current total quantity for a groupID by summing directly from cartItems.
     * cartItems is always up-to-date after any qty change or removal — unlike
     * minProductQty which is only loaded from localStorage on page init.
     */
    _getTotalQtyForGroup(key) {
        let total = 0;
        for (const item of this.cartItems) {
            if (item && item.properties?._groupID == key && !item.title?.includes('Associated')) {
                total += item.quantity || 0;
            }
        }
        return total;
    },

    /**
     * Calculate Running Fee for a given groupID key.
     * Returns the new running fee value (0 if unchanged or not applicable).
     */
    _calcRunningFee(key, value) {
        const detail = this._calcRunningFeeDetailed(key, value);
        return detail.total;
    },

    /**
     * Same as _calcRunningFee but also returns qty/multiplier/rate breakdown
     * for display ("40 x $1.50 = $60.00"). When multiple imprint locations
     * each contribute additional colours, multiplier sums across locations.
     */
    _calcRunningFeeDetailed(key, value) {
        let newRunningFee = 0;
        let totalMultiplier = 0;

        if (!Object.hasOwn(this.properties, key)) return { qty: 0, multiplier: 0, rate: 0, total: 0 };

        const totalQty = this._getTotalQtyForGroup(key);
        const runningFeePerItem = this.properties[key]._runningFeePerItem || 0;

        if (Object.hasOwn(this.properties[key], '_Imprint Lines') &&
            Array.isArray(this.properties[key]['_Imprint Lines'])) {

            this.properties[key]['_Imprint Lines'].forEach((location, locIndex) => {
                let uniqueColors = [];

                if (location.lines && Array.isArray(location.lines)) {
                    location.lines.forEach((line, index) => {
                        const lineNum = index + 1;
                        const colorKey = `Line ${lineNum} color`;
                        const colorSKUKey = `Line ${lineNum} Color SKU`;
                        if (line[colorKey] && line[colorSKUKey] && !uniqueColors.includes(line[colorKey])) {
                            uniqueColors.push(line[colorKey]);
                        }
                        if (line[colorKey] && !line[colorSKUKey] && !uniqueColors.includes(line[colorKey])) {
                            uniqueColors.push(line[colorKey]);
                        }
                    });
                }
                if (location['Image Data'] && location['Image Data']['Image Color']) {
                    if (location['Image Data']['Image Color SKU'] && !uniqueColors.includes(location['Image Data']['Image Color'])) {
                        uniqueColors.push(location['Image Data']['Image Color']);
                    }
                }

                const colorsCount = uniqueColors.length;
                if (colorsCount > 1) {
                    totalMultiplier += (colorsCount - 1);
                    newRunningFee += totalQty * runningFeePerItem * (colorsCount - 1);
                }
            });

        } else {
            // Old format: single location
            const lineItems = [];
            for (let [k, lineValue] of Object.entries(this.properties[key])) {
                if (k.includes('Line') && k.includes('color')) {
                    const lineNumMatch = k.match(/Line (\d+) color/);
                    if (lineNumMatch) {
                        const colorSKUKey = `Line ${lineNumMatch[1]} Color SKU`;
                        if (Object.hasOwn(this.properties[key], colorSKUKey) && !lineItems.includes(lineValue)) {
                            lineItems.push(lineValue);
                        }
                    }
                }
            }
            if (totalQty && runningFeePerItem && lineItems.length > 1) {
                totalMultiplier = lineItems.length - 1;
                newRunningFee = totalQty * runningFeePerItem * (lineItems.length - 1);
            }
        }

        // Effective qty for display: totalQty repeated once per extra colour
        // ("40 x $1.50" means 40 pieces charged once per additional colour).
        // When multiplier > 1 (several extra colours), fold it into displayedQty
        // so displayedQty * rate === total stays true.
        const displayedQty = totalMultiplier > 0 ? totalQty * totalMultiplier : 0;

        return { qty: displayedQty, multiplier: totalMultiplier, rate: runningFeePerItem, total: newRunningFee };
    },

    /**
     * Calculate Additional Location Fee for a given groupID key.
     * Returns the new fee total (null if not applicable or unchanged).
     */
    _calcAdditionalLocationFee(key, value) {
        const detail = this._calcAdditionalLocationFeeDetailed(key, value);
        if (detail === null) return null;

        const oldFeeTotal = this._getFeeValue(value.properties, 'Additional Location Fee');
        return detail.total === parseFloat(oldFeeTotal.toFixed(2)) ? null : detail.total;
    },

    /**
     * Same as _calcAdditionalLocationFee but also returns qty/rate breakdown
     * for display ("80 x $2.50 = $200.00"). Returns null if not applicable.
     */
    _calcAdditionalLocationFeeDetailed(key, value) {
        const oldFeeTotal = this._getFeeValue(value.properties, 'Additional Location Fee');
        if (oldFeeTotal <= 0) return null;

        const newQty = this._getTotalQtyForGroup(key);
        if (newQty <= 0) return null;

        const tiers = this.properties[key]?._price_details?.additional_location_fee_tiers || [];
        if (!tiers.length) return null;

        const imprintLines = this.properties[key]?.['_Imprint Lines'];
        const additionalLocationsCount = Array.isArray(imprintLines) && imprintLines.length > 1
            ? imprintLines.length - 1
            : 1;

        const sortedTiers = [...tiers].sort((a, b) => b.fee_qtymin - a.fee_qtymin);
        let feePerPiece = sortedTiers[sortedTiers.length - 1]?.fee_value || 0;
        for (const tier of sortedTiers) {
            if (newQty >= tier.fee_qtymin) { feePerPiece = tier.fee_value; break; }
        }
        if (feePerPiece <= 0) return null;

        const displayedQty = newQty * additionalLocationsCount;
        const newFeeTotal = parseFloat((feePerPiece * displayedQty).toFixed(2));

        return { qty: displayedQty, rate: feePerPiece, total: newFeeTotal };
    },

    /**
     * The raw cart item property is always literally "Custom Colour Fee ..."
     * for OLD carts (legacy, pre country-aware payload). New carts write the
     * region-correct spelling directly (see payloadBuilders.js). This handles
     * both: exact match is a no-op, and old British-spelled keys get
     * translated for US display.
     */
    displayFeeName(key) {
        if (key.startsWith('Custom Colour Fee') && window.Shopify.country !== 'CA') {
            return key.replace('Custom Colour Fee', 'Custom Color Fee');
        }
        return key;
    },

    _getFeeValue(properties, key) {
        const subtotalKey = `${key} Subtotal`;
        let raw;
        if (Object.hasOwn(properties, subtotalKey)) {
            raw = properties[subtotalKey];
        } else if (Object.hasOwn(properties, key)) {
            raw = properties[key];
        } else if (key === 'Custom Color Fee') {
            if (Object.hasOwn(properties, 'Custom Colour Fee Subtotal')) raw = properties['Custom Colour Fee Subtotal'];
            else if (Object.hasOwn(properties, 'Custom Colour Fee')) raw = properties['Custom Colour Fee'];
        } else if (key === 'Custom Colour Fee') {
            if (Object.hasOwn(properties, 'Custom Color Fee Subtotal')) raw = properties['Custom Color Fee Subtotal'];
            else if (Object.hasOwn(properties, 'Custom Color Fee')) raw = properties['Custom Color Fee'];
        }
        if (raw === null || raw === undefined) return 0;
        if (typeof raw === 'string') {
            // Legacy breakdown format ("5 x $30.00 = $150.00") — pull the total after "="
            const match = raw.match(/=\s*\$?([\d.]+)\s*$/);
            if (match) return Number(match[1]) || 0;
        }
        return Number(raw) || 0;
    },

    /**
     * Build table rows for the Associated Fees table from flat properties.
     * Supports both the new format ("Setup Fee Qty"/"Unit Price"/"Subtotal")
     * and legacy formats (plain number or "N x $P = $T" breakdown string) via
     * the same _getFeeValue() fallback used elsewhere.
     */
    getFeeRows(properties) {
        const rows = [];
        for (const baseName of Object.keys(this.assocPropsEmpty)) {
            const subtotalKey = `${baseName} Subtotal`;
            let qty = null;
            let unitPrice = null;
            let subtotal = 0;

            if (Object.hasOwn(properties, subtotalKey)) {
                subtotal = Number(properties[subtotalKey]) || 0;
                qty = Object.hasOwn(properties, `${baseName} Qty`) ? properties[`${baseName} Qty`] : null;
                unitPrice = Object.hasOwn(properties, `${baseName} Unit Price`)
                    ? Number(properties[`${baseName} Unit Price`])
                    : null;
            } else {
                subtotal = this._getFeeValue(properties, baseName);
            }

            if (subtotal > 0) {
                rows.push({ name: baseName, qty, unitPrice, subtotal });
            }
        }
        return rows;
    },

    aggregatedFees() {
        const totals = {};
        // Initialize all keys from assocPropsEmpty
        Object.keys(this.assocPropsEmpty).forEach(key => {
            totals[key] = 0;
        });
        // Accumulate values from each group
        Object.values(this.assocToProduct).forEach(item => {
            Object.keys(this.assocPropsEmpty).forEach(key => {
                totals[key] += this._getFeeValue(item.properties, key);
            });
        });
        return totals;
    },

    /**
     * Recalculate Running Fee + Additional Location Fee together per groupID.
     * Makes a single changeCartLine call per group.
     */
    recalculateDynamicFees(groupID = null) {
        for (const [key, value] of Object.entries(this.assocToProduct)) {

            // Only recalculate the group whose qty changed
            if (groupID && key !== groupID) continue;

            let changed = false;

            // --- Running Fee ---
            const currentRunningFee = this._getFeeValue(value.properties, 'Running Fee');
            const runningDetail = this._calcRunningFeeDetailed(key, value);
            if (runningDetail.total !== currentRunningFee) {
                if (runningDetail.qty > 0 && runningDetail.rate > 0) {
                    value.properties['Running Fee Qty'] = String(runningDetail.qty);
                    value.properties['Running Fee Unit Price'] = runningDetail.rate.toFixed(2);
                    value.properties['Running Fee Subtotal'] = runningDetail.total.toFixed(2);
                    delete value.properties['Running Fee']; // drop legacy key to avoid duplicate row
                } else {
                    value.properties['Running Fee'] = runningDetail.total.toFixed(2);
                    delete value.properties['Running Fee Qty'];
                    delete value.properties['Running Fee Unit Price'];
                    delete value.properties['Running Fee Subtotal'];
                }
                changed = true;
            }

            // --- Additional Location Fee ---
            const addLocDetail = this._calcAdditionalLocationFeeDetailed(key, value);
            if (addLocDetail !== null) {
                const oldAddLocFeeTotal = this._getFeeValue(value.properties, 'Additional Location Fee');
                if (addLocDetail.total !== parseFloat(oldAddLocFeeTotal.toFixed(2))) {
                    if (addLocDetail.qty >= 1 && addLocDetail.rate > 0) {
                        value.properties['Additional Location Fee Qty'] = String(addLocDetail.qty);
                        value.properties['Additional Location Fee Unit Price'] = addLocDetail.rate.toFixed(2);
                        value.properties['Additional Location Fee Subtotal'] = addLocDetail.total.toFixed(2);
                        delete value.properties['Additional Location Fee'];
                    } else {
                        value.properties['Additional Location Fee'] = addLocDetail.total.toFixed(2);
                        delete value.properties['Additional Location Fee Qty'];
                        delete value.properties['Additional Location Fee Unit Price'];
                        delete value.properties['Additional Location Fee Subtotal'];
                    }
                    changed = true;
                }
            }

            if (!changed) continue;

            const newTotal = Object.keys(this.assocPropsEmpty).reduce((sum, k) => {
                return sum + this._getFeeValue(value.properties, k);
            }, 0);

            this.updateAssocFeeLinePrice(key, value, newTotal);
        }
    },

    // Keep these as aliases for backwards compatibility if called elsewhere
    calculateRunningFee() { this.recalculateDynamicFees(); },
    calculateAdditionalLocationFee() { this.recalculateDynamicFees(); },

    /**
     * Shared helper: update assocToProduct linePriceProperty, sync cartItems, call changeCartLine.
     */
    updateAssocFeeLinePrice(key, value, newTotal) {
        value[this.linePriceProperty] = newTotal * 100;
        this.assocToProduct[key] = { ...value };

        for (let item of this.cartItems) {
            if (item.title.includes('Associated') && item.properties._groupID == key) {
                item[this.linePriceProperty] = value[this.linePriceProperty];
                Object.assign(item.properties, value.properties);
                this.changeCartLine(item);
            }
        }
    },

    /**
     * Shared helper: sum all fee properties from assocPropsEmpty EXCEPT the excluded key.
     */
    sumAssocFeesExcluding(properties, excludeKey) {
        let total = 0;
        for (const assocPropsKey of Object.keys(this.assocPropsEmpty)) {
            if (assocPropsKey === excludeKey) continue;
            total += this._getFeeValue(properties, assocPropsKey);
        }
        return total;
    },

    calculateFee() {
        Object.keys(this.assocPropsEmpty).forEach(k => this.assocPropsEmpty[k] = 0);
        for (const [key, value] of Object.entries(this.assocToProduct)) {
            for (const assocPropsKey of Object.keys(this.assocPropsEmpty)) {
                const current = Number.parseFloat(this.assocPropsEmpty[assocPropsKey]) || 0;
                this.assocPropsEmpty[assocPropsKey] = (current + this._getFeeValue(value.properties, assocPropsKey)).toFixed(2);
            }
        }
    },
};
